<template lang="pug">
.reader-tools(@keydown.esc="closePanel")
  .reader-page-count
    input(aria-label="目前頁碼" type="number" min="1" :max="pages.length" :value="currentPage + 1" @change="jump($event.target.value)")
    span / {{ pages.length }}
  button.reader-arrow.reader-prev(type="button" aria-label="上一頁" :disabled="currentPage === 0" @click="navigate(currentPage - 1)") ‹
  button.reader-arrow.reader-next(type="button" aria-label="下一頁" :disabled="currentPage >= pages.length - 1" @click="navigate(currentPage + 1)") ›
  .reader-notice(v-if="notice" role="status") {{ notice }}
  section.reader-panel(v-if="panel" :aria-label="panelTitle")
    header
      strong {{ panelTitle }}
      button(type="button" aria-label="關閉面板" @click="closePanel") ×
    .reader-thumbnails(v-if="panel === 'thumbs'")
      button(v-for="(page, index) in pages" :key="page.id" type="button" :aria-label="`跳至第 ${index + 1} 頁`" :aria-current="index === currentPage ? 'page' : null" @click="navigate(index)")
        img(:src="page.thumb" :alt="`第 ${index + 1} 頁縮圖`" loading="lazy")
        span {{ index + 1 }}
    .reader-bookmarks(v-else-if="panel === 'bookmarks'")
      button(type="button" @click="toggleBookmark") {{ bookmarks.includes(currentPage) ? '移除此頁書籤' : '將此頁加入書籤' }}
      p(v-if="!bookmarks.length") 尚未加入書籤
      .reader-bookmark(v-for="page in bookmarks" :key="page")
        button(type="button" @click="navigate(page)") 第 {{ page + 1 }} 頁
        button(type="button" :aria-label="`移除第 ${page + 1} 頁書籤`" @click="removeBookmark(page)") ×
    .reader-share(v-else-if="panel === 'share'")
      label(for="monthly-share-url") 此頁連結
      input#monthly-share-url(:value="shareUrl" readonly @focus="$event.target.select()")
      button(type="button" @click="copyLink") 複製連結
      button(v-if="nativeShare" type="button" @click="share") 分享…
    .reader-more(v-else-if="panel === 'more'")
      button(type="button" @click="navigate(0)") 第一頁
      button(type="button" @click="navigate(pages.length - 1)") 最後一頁
      button(type="button" @click="$emit('zoom', 1); closePanel()") 恢復原尺寸
      button(type="button" :disabled="downloading" @click="downloadPage") {{ downloading ? '下載中…' : '下載此頁' }}
      button(type="button" @click="printPage") 列印此頁
  nav.reader-toolbar(aria-label="月刊控制台")
    button(v-for="tool in tools" :key="tool.id" type="button" :aria-label="tool.label" :title="tool.label" :disabled="tool.disabled" :aria-pressed="tool.active" @click="activate(tool.id)")
      svg(viewBox="0 0 24 24" aria-hidden="true")
        path(:d="tool.path")
</template>

<script>
const icons = {
  minus: 'M5 11h14v2H5z',
  plus: 'M11 4h2v7h7v2h-7v7h-2v-7H4v-2h7z',
  thumbs: 'M3 3h18v18H3V3zm3 3v5h5V6H6zm7 0v5h5V6h-5zm-7 7v5h5v-5H6zm7 0v5h5v-5h-5z',
  play: 'M6 3v18l15-9L6 3z',
  pause: 'M5 3h5v18H5zm9 0h5v18h-5z',
  bookmarks: 'M5 2h14v21l-7-5-7 5V2z',
  share: 'M18 2a3 3 0 1 1-2.6 4.5L8.8 10a3 3 0 0 1 0 4l6.6 3.5a3 3 0 1 1-.9 1.8L8 15.8a4 4 0 1 1 0-7.6l6.5-3.5A3 3 0 0 1 18 2z',
  more: 'M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 8a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 8a2 2 0 1 1 0 4 2 2 0 0 1 0-4z',
  fullscreen: 'M3 3h7v3H6v4H3V3zm11 0h7v7h-3V6h-4V3zM3 14h3v4h4v3H3v-7zm15 0h3v7h-7v-3h4v-4z',
  exit: 'M7 3h3v7H3V7h4V3zm7 0h3v4h4v3h-7V3zM3 14h7v7H7v-4H3v-3zm11 0h7v3h-4v4h-3v-7z'
}
export default {
  name: 'ReaderToolbar',
  props: {
    pages: { type: Array, required: true },
    currentPage: { type: Number, required: true },
    zoom: { type: Number, required: true },
    bookId: { type: [String, Number], required: true },
    title: { type: String, default: '東震月刊' },
    readerElement: { type: Function, required: true }
  },
  data () {
    return { panel: '', bookmarks: [], playing: false, timer: null, notice: '', noticeTimer: null, nativeShare: false, fullscreen: false, downloading: false }
  },
  computed: {
    storageKey () { return `tocin-monthly-bookmarks-${this.bookId}` },
    shareUrl () {
      const url = new URL(window.location.href)
      url.searchParams.set('page', this.currentPage + 1)
      return url.toString()
    },
    panelTitle () { return { thumbs: '頁面縮圖', bookmarks: '書籤', share: '分享此頁', more: '更多功能' }[this.panel] },
    tools () {
      return [
        { id: 'minus', label: '縮小', disabled: this.zoom <= 1 }, { id: 'plus', label: '放大', disabled: this.zoom >= 3 },
        { id: 'thumbs', label: '頁面縮圖', active: this.panel === 'thumbs' },
        { id: 'play', label: this.playing ? '暫停自動翻頁' : '播放自動翻頁', active: this.playing },
        { id: 'bookmarks', label: '書籤', active: this.panel === 'bookmarks' },
        { id: 'share', label: '分享', active: this.panel === 'share' },
        { id: 'more', label: '更多功能', active: this.panel === 'more' },
        { id: 'fullscreen', label: this.fullscreen ? '離開全螢幕' : '全螢幕', active: this.fullscreen }
      ].map(tool => ({ ...tool, path: icons[tool.id === 'play' && this.playing ? 'pause' : tool.id === 'fullscreen' && this.fullscreen ? 'exit' : tool.id] }))
    }
  },
  mounted () {
    this.nativeShare = typeof navigator.share === 'function'
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || '[]')
      this.bookmarks = Array.isArray(saved) ? [...new Set(saved.filter(p => Number.isInteger(p) && p >= 0 && p < this.pages.length))].sort((a, b) => a - b) : []
    } catch (e) { this.bookmarks = [] }
    this.onVisibility = () => { if (document.hidden) this.stopPlayback() }
    this.onFullscreen = () => { this.fullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement) }
    document.addEventListener('visibilitychange', this.onVisibility)
    document.addEventListener('fullscreenchange', this.onFullscreen)
    document.addEventListener('webkitfullscreenchange', this.onFullscreen)
  },
  beforeDestroy () {
    this.stopPlayback()
    clearTimeout(this.noticeTimer)
    document.removeEventListener('visibilitychange', this.onVisibility)
    document.removeEventListener('fullscreenchange', this.onFullscreen)
    document.removeEventListener('webkitfullscreenchange', this.onFullscreen)
  },
  methods: {
    message (text) { this.notice = text; clearTimeout(this.noticeTimer); this.noticeTimer = setTimeout(() => { this.notice = '' }, 5000) },
    closePanel () { this.panel = '' },
    activate (id) {
      if (id === 'minus' || id === 'plus') { this.$emit('zoom', Math.max(1, Math.min(3, this.zoom + (id === 'plus' ? 0.25 : -0.25)))); return }
      if (id === 'play') { this.togglePlayback(); return }
      if (id === 'fullscreen') { this.toggleFullscreen(); return }
      this.stopPlayback()
      this.panel = this.panel === id ? '' : id
    },
    navigate (index) { this.stopPlayback(); this.closePanel(); this.$emit('go', Math.max(0, Math.min(this.pages.length - 1, index))) },
    jump (value) { const page = Number(value); if (Number.isInteger(page)) this.navigate(page - 1) },
    stopPlayback () { clearInterval(this.timer); this.timer = null; this.playing = false },
    togglePlayback () {
      if (this.playing) { this.stopPlayback(); return }
      this.closePanel(); this.playing = true
      if (this.currentPage >= this.pages.length - 1) this.$emit('go', 0)
      this.timer = setInterval(() => {
        if (this.currentPage >= this.pages.length - 1) { this.stopPlayback(); return }
        this.$emit('go', this.currentPage + 1)
      }, 4000)
    },
    saveBookmarks () { try { localStorage.setItem(this.storageKey, JSON.stringify(this.bookmarks)) } catch (e) { this.message('此瀏覽器無法儲存書籤，關閉後將不保留。') } },
    toggleBookmark () {
      if (this.bookmarks.includes(this.currentPage)) { this.removeBookmark(this.currentPage); return }
      this.bookmarks = [...this.bookmarks, this.currentPage].sort((a, b) => a - b); this.saveBookmarks()
    },
    removeBookmark (page) { this.bookmarks = this.bookmarks.filter(p => p !== page); this.saveBookmarks() },
    async copyLink () {
      try { await navigator.clipboard.writeText(this.shareUrl); this.message('已複製此頁連結') } catch (e) { this.message('請長按上方連結，選取並複製。') }
    },
    async share () {
      try { await navigator.share({ title: this.title, url: this.shareUrl }) } catch (e) { if (e.name !== 'AbortError') this.message('請改用複製連結分享。') }
    },
    async toggleFullscreen () {
      try {
        if (this.fullscreen) {
          const exit = document.exitFullscreen || document.webkitExitFullscreen
          if (exit) await exit.call(document)
        } else {
          const reader = this.readerElement()
          const enter = reader && (reader.requestFullscreen || reader.webkitRequestFullscreen)
          if (!enter) { this.message('此瀏覽器不支援網頁全螢幕，請使用瀏覽器的隱藏工具列功能。'); return }
          await enter.call(reader)
        }
      } catch (e) { this.message('無法進入全螢幕，請使用瀏覽器的隱藏工具列功能。') }
    },
    async downloadPage () {
      this.downloading = true
      const pageIndex = this.currentPage
      try {
        const response = await fetch(this.pages[pageIndex].src)
        if (!response.ok) throw new Error('Download failed')
        const url = URL.createObjectURL(await response.blob())
        const link = document.createElement('a')
        link.href = url; link.download = `monthly-${this.bookId}-${pageIndex + 1}.jpg`
        document.body.appendChild(link); link.click(); link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) { this.message('圖片下載失敗，請稍後重試。') } finally { this.downloading = false }
    },
    printPage () {
      const popup = window.open('', '_blank')
      if (!popup) { this.message('請允許彈出視窗後再列印。'); return }
      popup.opener = null
      popup.document.title = `${this.title} — ${this.currentPage + 1}`
      const img = popup.document.createElement('img')
      img.style.cssText = 'display:block;max-width:100%;max-height:95vh;margin:auto'
      img.onload = () => { popup.focus(); popup.print() }
      img.onerror = () => { popup.document.body.textContent = '圖片載入失敗，請關閉後重試。' }
      img.src = this.pages[this.currentPage].src
      popup.document.body.appendChild(img)
    }
  }
}
</script>

<style scoped>
.reader-tools{touch-action:manipulation;color:#222}
.reader-tools button{touch-action:manipulation;font:inherit;cursor:pointer}.reader-tools button:disabled{opacity:.2;cursor:default}
.reader-toolbar{position:absolute;bottom:0;left:0;right:0;display:flex;justify-content:center;background:#fff;border-top:1px solid #ddd;padding:0 6px env(safe-area-inset-bottom,0px);height:calc(56px + env(safe-area-inset-bottom,0px));box-sizing:border-box;z-index:5}
.reader-toolbar button{flex:1 1 0;min-width:0;max-width:56px;height:55px;border:0;background:transparent;color:#292b2c;padding:12px 8px}
.reader-toolbar svg{display:block;width:24px;height:24px;fill:currentColor;margin:auto}.reader-toolbar button[aria-pressed=true]{color:#176d48;background:#edf5f0}
.reader-toolbar button:focus-visible,.reader-panel button:focus-visible{outline:2px solid #176d48;outline-offset:-3px}
.reader-page-count{position:absolute;top:0;left:0;background:#0008;color:white;padding:6px 10px;z-index:2;font-size:13px;display:flex;align-items:center;gap:4px}
.reader-page-count input{width:3em;font-size:16px;background:transparent;border:0;color:inherit;text-align:right;appearance:textfield}.reader-page-count input::-webkit-inner-spin-button{appearance:none}
.reader-arrow{position:absolute;top:calc(50% - 28px);z-index:2;background:#0005;color:white;border:0;font-size:36px!important;width:32px;height:60px;border-radius:4px;padding:0}.reader-prev{left:12px}.reader-next{right:12px}.reader-arrow:disabled{visibility:hidden}
.reader-panel{position:absolute;bottom:calc(56px + env(safe-area-inset-bottom,0px));left:0;right:0;max-height:65%;overflow:auto;overscroll-behavior:contain;background:#fff;z-index:4;padding:16px;box-shadow:0 -3px 16px #0003;text-align:left}
.reader-panel header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}.reader-panel header button{border:0;background:none;font-size:28px;min-width:44px;min-height:44px}
.reader-panel button{min-height:44px;border:1px solid #ddd;border-radius:4px;background:#f7f7f7;color:#222;padding:8px 12px}.reader-panel input{width:100%;padding:10px;font-size:16px;margin-bottom:12px}
.reader-thumbnails{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.reader-thumbnails button{display:flex;flex-direction:column;gap:6px;align-items:center}.reader-thumbnails img{width:100%;height:100px;object-fit:contain}.reader-thumbnails [aria-current=page]{border:2px solid #176d48}
.reader-bookmark{display:flex;justify-content:space-between;margin-top:8px}.reader-more{display:grid;gap:8px}.reader-share button{margin-right:8px}.reader-notice{position:absolute;bottom:calc(68px + env(safe-area-inset-bottom,0px));left:12px;right:12px;background:#222;color:white;padding:12px;z-index:6;border-radius:4px}
</style>
